import { useEffect, useMemo, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Users, Plus, Search, Trash2, Edit2, X, Save, Upload, Download, Link as LinkIcon, DollarSign, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const emptyForm = {
  contact_name: "",
  business_name: "",
  email: "",
  phone: "",
  service: "",
  service_value: 0,
  closed_won_date: new Date().toISOString().slice(0, 10),
  lead_source: "",
  notes: "",
};

const LEAD_SOURCES = ["Self Generated", "Bark", "Firmable", "Partner", "Website", "Referral", "Networking Event", "Social Media", "Other"];

const currency = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(v || 0);

const SOURCE_STYLES = {
  opportunity: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  manual: "bg-slate-100 text-slate-700 border border-slate-200",
  import: "bg-indigo-50 text-indigo-700 border border-indigo-200",
};

export default function ClientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [overview, setOverview] = useState({ total: 0, total_value: 0, from_opportunity: 0, manual: 0, imported: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [service, setService] = useState("");
  const [leadSource, setLeadSource] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [toDelete, setToDelete] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (service) params.service = service;
      if (leadSource) params.lead_source = leadSource;
      const [r, ov] = await Promise.all([
        api.get("/clients", { params }),
        api.get("/clients/summary/overview"),
      ]);
      setRows(r.data);
      setOverview(ov.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [q, service, leadSource]);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm }); setShowForm(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      contact_name: row.contact_name || "",
      business_name: row.business_name || "",
      email: row.email || "",
      phone: row.phone || "",
      service: row.service || "",
      service_value: row.service_value || 0,
      closed_won_date: row.closed_won_date || "",
      lead_source: row.lead_source || "",
      notes: row.notes || "",
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.contact_name.trim()) { toast.error("Contact name is required"); return; }
    try {
      const payload = { ...form, service_value: Number(form.service_value) || 0 };
      if (editing) {
        await api.patch(`/clients/${editing.id}`, payload);
        toast.success("Client updated");
      } else {
        await api.post("/clients", payload);
        toast.success("Client added");
      }
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/clients/${toDelete.id}`, { headers: { "X-Confirm-Delete": "DELETE" } });
      toast.success("Client deleted");
      setToDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doImport = async () => {
    if (!importFile) { toast.error("Choose a file first"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.inserted} clients${r.data.skipped ? `, skipped ${r.data.skipped}` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/clients/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "clients_import_template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await api.post("/clients/backfill-closed-won");
      toast.success(`Processed ${r.data.processed} Closed Won opportunities — ${r.data.created} new clients added`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBackfilling(false); }
  };

  const goToOpp = (oppId) => { if (oppId) navigate(`/opportunities/${oppId}`); };

  return (
    <div className="p-8" data-testid="clients-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyb-label">Clients</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Users className="w-7 h-7 text-[#1B4F8B]" />
            Clients
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every Closed Won opportunity flows in here automatically. Add existing clients manually or import them in bulk.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="clients-view-report-btn"
            variant="outline"
            onClick={() => navigate("/reports?tab=clients-source")}
          >
            <BarChart3 className="w-4 h-4 mr-1" /> View Report
          </Button>
          {isAdmin && (
            <Button data-testid="clients-backfill-btn" variant="outline" onClick={runBackfill} disabled={backfilling}>
              {backfilling ? "Working…" : "Sync from Closed Won"}
            </Button>
          )}
          <Button data-testid="clients-template-btn" variant="outline" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-1" /> Template
          </Button>
          <Button data-testid="clients-import-btn" variant="outline" onClick={() => { setImportFile(null); setImportResult(null); setShowImport(true); }}>
            <Upload className="w-4 h-4 mr-1" /> Import
          </Button>
          <Button data-testid="new-client-btn" onClick={openCreate} className="bg-[#1B4F8B] hover:bg-[#163e6e]">
            <Plus className="w-4 h-4 mr-1" /> New Client
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="clients-overview">
        <div className="eyb-card p-4">
          <div className="eyb-label">Total Clients</div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{overview.total || 0}</div>
        </div>
        <div className="eyb-card p-4">
          <div className="eyb-label">Total Value</div>
          <div className="font-heading text-2xl font-bold text-emerald-700 mt-1">{currency(overview.total_value)}</div>
        </div>
        <div className="eyb-card p-4">
          <div className="eyb-label">From Closed Won</div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{overview.from_opportunity || 0}</div>
        </div>
        <div className="eyb-card p-4">
          <div className="eyb-label">Manual / Imported</div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{(overview.manual || 0) + (overview.imported || 0)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="eyb-card p-4 mb-4 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="clients-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, business, email, service…"
            className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
          />
        </div>
        <input
          data-testid="clients-filter-service"
          value={service}
          onChange={(e) => setService(e.target.value)}
          placeholder="Filter by service"
          className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white w-56"
        />
        <select
          data-testid="clients-filter-lead-source"
          value={leadSource}
          onChange={(e) => setLeadSource(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white w-52"
        >
          <option value="">All lead sources</option>
          {LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* List */}
      <div className="eyb-card overflow-hidden" data-testid="clients-list">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60 border-b border-slate-200">
              <th className="px-4 py-3 font-semibold">Client</th>
              <th className="px-4 py-3 font-semibold">Business</th>
              <th className="px-4 py-3 font-semibold">Service</th>
              <th className="px-4 py-3 font-semibold text-right">Service Value</th>
              <th className="px-4 py-3 font-semibold">Closed Won</th>
              <th className="px-4 py-3 font-semibold">Lead Source</th>
              <th className="px-4 py-3 font-semibold">Source</th>
              <th className="px-4 py-3 font-semibold w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="py-6 text-center text-slate-400 text-xs">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="py-10 text-center text-slate-400 text-sm">No clients yet. Add one manually, import from Excel/CSV, or click <span className="font-semibold">Sync from Closed Won</span> to pull in existing wins.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`client-row-${r.id}`}>
                <td className="px-4 py-3">
                  <button className="font-semibold text-slate-900 hover:text-[#1f8ebf] text-left" onClick={() => openEdit(r)}>{r.contact_name}</button>
                  {r.email && <div className="text-xs text-slate-500">{r.email}</div>}
                  {r.phone && <div className="text-xs text-slate-500">{r.phone}</div>}
                </td>
                <td className="px-4 py-3 text-slate-700">{r.business_name || "—"}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{r.service || "—"}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900">{currency(r.service_value)}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{r.closed_won_date ? fmtDate(r.closed_won_date) : "—"}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{r.lead_source || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${SOURCE_STYLES[r.source] || SOURCE_STYLES.manual}`}>
                    {r.source === "opportunity" ? "Auto (Opp)" : r.source === "import" ? "Imported" : "Manual"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {r.opportunity_id && (
                      <button onClick={() => goToOpp(r.opportunity_id)} data-testid={`client-goto-opp-${r.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Open originating opportunity">
                        <LinkIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => openEdit(r)} data-testid={`client-edit-${r.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Edit">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    {isAdmin && (
                      <button onClick={() => setToDelete(r)} data-testid={`client-delete-${r.id}`} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl" data-testid="client-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Client" : "New Client"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update client details, service or amount." : "Add an existing client that closed outside the CRM. New wins from Opportunities will appear here automatically."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Contact Name *</Label>
              <Input data-testid="client-form-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            </div>
            <div>
              <Label>Business Name</Label>
              <Input data-testid="client-form-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="client-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="client-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Service Provided</Label>
              <Input data-testid="client-form-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="e.g. Consulting, Advisory" />
            </div>
            <div>
              <Label>Service Value (AUD)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="client-form-value" type="number" min="0" step="0.01" value={form.service_value} onChange={(e) => setForm({ ...form, service_value: e.target.value })} className="pl-9" />
              </div>
            </div>
            <div>
              <Label>Closed Won Date</Label>
              <Input data-testid="client-form-date" type="date" value={form.closed_won_date} onChange={(e) => setForm({ ...form, closed_won_date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Lead Source</Label>
              <select
                data-testid="client-form-lead-source"
                value={form.lead_source}
                onChange={(e) => setForm({ ...form, lead_source: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
              >
                <option value="">— Unspecified —</option>
                {LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea data-testid="client-form-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button data-testid="client-form-save" onClick={save} className="bg-[#1B4F8B] hover:bg-[#163e6e]">
              <Save className="w-4 h-4 mr-1" /> {editing ? "Save changes" : "Add Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-lg" data-testid="clients-import-dialog">
          <DialogHeader>
            <DialogTitle>Import Clients</DialogTitle>
            <DialogDescription>
              Upload a CSV or Excel (.xlsx) with columns: <span className="font-mono text-xs">contact_name, business_name, email, phone, service, service_value, closed_won_date, lead_source, notes</span>.{" "}
              <button className="text-[#1f8ebf] underline" onClick={downloadTemplate}>Download template</button>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>File</Label>
              <input
                data-testid="clients-import-file"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="w-full text-sm border border-slate-200 rounded-md p-2 bg-white"
              />
            </div>
            {importResult && (
              <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3">
                <div className="font-semibold text-slate-800">Imported {importResult.inserted}{importResult.skipped ? `, skipped ${importResult.skipped}` : ""}</div>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 text-rose-700 list-disc pl-5">
                    {importResult.errors.slice(0, 5).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                    {importResult.errors.length > 5 && <li>… and {importResult.errors.length - 5} more</li>}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)}>Close</Button>
            <Button data-testid="clients-import-run" onClick={doImport} disabled={!importFile || importing} className="bg-[#1B4F8B] hover:bg-[#163e6e]">
              <Upload className="w-4 h-4 mr-1" /> {importing ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="client-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this client?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span className="font-semibold">{toDelete?.contact_name}</span> from Clients. The originating opportunity (if any) will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="client-delete-confirm" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
